import { Markdown } from '@squareup/dex-feature-markdown';
import { useShowMarkdown } from '@squareup/dex-store-access-dex-tech-docs-selectors';
import { DocPage, NavSet } from '@squareup/dex-types-shared-docs';
import { NextStepBlock, TabAnimator, Tabset, Head } from '@squareup/dex-ui';
import { DocsPageContentLayout } from '@squareup/dex-ui-dex-tech-docs-page-layout';
import { Box } from '@squareup/dex-ui-shared-base';
import { MarkdownTocProvider } from '@squareup/dex-ui-shared-markdown-toc';
import { FloatingToc } from '@squareup/dex-ui-shared-table-of-contents';
import { useLeftNavItemsForPage } from '@squareup/dex-utils-docs-navigation';
import { getNextPublicDocsNameOrDefault } from '@squareup/dex-utils-environment';
import React, { FC, Fragment, useCallback, useRef, useState } from 'react';

import { DocFeedbackPrompt } from './DocFeedbackPrompt';
import { DocPageHeading } from './DocPageHeading';
import styles from './content.module.css';
import {
  useBreadcrumbs,
  useMarkdownNodes,
  useMarkdownNodesSectionedByLanguage,
  useMultiLanguages,
  useNextStepBlocks,
  useTocNode,
  useTocSection,
} from './markdown-block-hooks';

interface DocsPageProps {
  docPage: DocPage;
  navSet?: NavSet | undefined;
  disableFeedback?: boolean | undefined;
}

const DocsPageContent: FC<DocsPageProps> = ({
  docPage,
  navSet,
  disableFeedback = false,
}) => {
  const markdownContentRef = useRef<HTMLElement>(null);
  const showMarkdown = useShowMarkdown();

  const languages = useMultiLanguages(docPage);
  const markdownNodes = useMarkdownNodes(docPage);

  // Make sure every tab has all the appropriate nodes. For example,
  // nodes without a language go into both sections
  const markdownNodesByLanguage = useMarkdownNodesSectionedByLanguage(
    markdownNodes,
    languages
  );

  const nextStepBlocks = useNextStepBlocks(docPage);

  const navItem = useLeftNavItemsForPage(docPage.leftNavId, navSet);
  const breadcrumbs = useBreadcrumbs(navItem, docPage.slug);

  const [currentTabId, setCurrentTabId] = useState<string | undefined>(
    languages?.[0] || undefined
  );

  const tocSections = useTocSection(markdownNodesByLanguage, currentTabId);
  const tocNode = useTocNode(markdownNodes);

  const onTabChanged = useCallback(
    (newTabId: string) => {
      setCurrentTabId(newTabId);
    },
    [setCurrentTabId]
  );

  const ftocSections = tocSections.filter((section) => section.level < 3);

  return (
    <DocsPageContentLayout
      floatingToc={
        !tocNode?.disabled &&
        ftocSections.length > 1 && (
          <FloatingToc
            sections={ftocSections}
            mainContentRef={markdownContentRef}
          />
        )
      }
    >
      <>
        <Head
          title={
            docPage?.heading?.browserTitle || getNextPublicDocsNameOrDefault()
          }
          description={
            docPage?.searchSummary || docPage?.heading?.summary || ''
          }
          canonicalPath={`/docs/${docPage.slug}`}
        />
        <DocPageHeading
          heading={docPage.heading}
          breadcrumbItems={breadcrumbs}
        />
        <Box ref={markdownContentRef}>
          {languages.length > 1 && (
            <Tabset
              tabs={languages.map((lang) => {
                return {
                  id: lang,
                  label: lang,
                };
              })}
              currentTabId={currentTabId || ''}
              onTabChanged={onTabChanged}
            ></Tabset>
          )}
          <TabAnimator
            currentTab={currentTabId || ''}
            tabs={markdownNodesByLanguage.map((tabContent) => {
              const contents = tabContent.nodes.map((node) => {
                const markdownFragment = showMarkdown ? (
                  <Box
                    margin={{ vertical: '2x' }}
                    as="pre"
                    className={styles['show-markdown-pre']}
                  >
                    {node.markdown}
                  </Box>
                ) : (
                  <Markdown renderTreeNode={node.node} />
                );

                return (
                  <Fragment key={node.key}>
                    <MarkdownTocProvider
                      value={{ sections: tabContent.tocSections }}
                    >
                      {markdownFragment}
                    </MarkdownTocProvider>
                  </Fragment>
                );
              });
              return {
                tabId: tabContent.language,
                contents,
              };
            })}
          />
          {nextStepBlocks && (
            <Box margin={{ top: '5x' }}>
              <NextStepBlock
                previous={nextStepBlocks.previousBlock}
                next={nextStepBlocks.nextBlock}
              />
            </Box>
          )}

          {!disableFeedback && <DocFeedbackPrompt />}
        </Box>
      </>
    </DocsPageContentLayout>
  );
};

DocsPageContent.displayName = 'DocsPageContent';

export { DocsPageContent };
